import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  type Playlist,
  PlaylistCreateData,
  PlaylistsWithPagination
} from '@shared/interfacres/playlist';
import { GetFilter, GetPagination } from '@shared/interfacres/request-related';
import { BaseApiService } from '@data-access/common/base-api.service';

@Injectable({providedIn: 'root'})
export class PlaylistsManagerService extends BaseApiService {

  getFiltered(
    name: string,
    filter: GetFilter,
    pagination: GetPagination
  ): Observable<PlaylistsWithPagination> {

    let apiUrl = `${this.apiUrl}/admin/settings/playlists/paginated?page=${pagination.page}&limit=${pagination.pageSize}&sortBy=${filter.query}&sortDirection=${filter.order}`;
    if (name) {
      apiUrl += `&search=${name}`;
    }
    return this._httpClient.get<PlaylistsWithPagination>(apiUrl).pipe(
      map((response: PlaylistsWithPagination) => this.returnResponseData(response)),
      catchError(this.handleError)
    )
  }

  getList()  {
    const requestUrl = `${this.apiUrl}/admin/settings/playlists`;
    return this._httpClient.get(requestUrl).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    )
  }

  getImageById(id: number) {
    const requestUrl = `${this.apiUrl}/admin/settings/playlists/public-image?id=${id}`;
    return this._httpClient.get(requestUrl, {responseType: 'text'});
  }

  getById(id: number): Observable<Playlist>  {
    const requestUrl = `${this.apiUrl}/admin/settings/playlists/${id}`;

    return this._httpClient.get(requestUrl).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    )
  }

  create(data: PlaylistCreateData) {
    const formData = new FormData();
    if (data.file) {
      formData.append('image', data.file, data.file.name);
    }

    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('isPublic', `${data.isPublic}`);


    const url = `${this.apiUrl}/admin/settings/playlists/with-image`;
    return this._httpClient.post(url, formData).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  updatePartial(id: number, payload: Partial<Playlist>) {
    const url = `${this.apiUrl}/admin/settings/playlists/${id}/with-image`;
    return this._httpClient.patch(url, payload).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  addTracksToPlaylists(trackId: number, playlistIds: number[]) {
    const url = `${this.apiUrl}/admin/settings/tracks/${trackId}/playlists`;
    return this._httpClient.post(url, {playlistIds}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  update(id: number, data: PlaylistCreateData) {
    const formData = new FormData();
    if (data.image === '') {
      formData.append('image', '');
    }
    if (data.file) {
      formData.append('image', data.file, data.file.name);
    }

    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('isPublic', `${data.isPublic}`);

    const url = `${this.apiUrl}/admin/settings/playlists/${id}/with-image`;
    return this._httpClient.patch(url, formData).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  delete(ids: number[]) {
    const url = `${this.apiUrl}/admin/settings/playlists`;
    return this._httpClient.delete(url, {body: {ids}}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  deleteTracksFromPlaylist(playlistId: number, trackIds: number[]) {
    const url = `${this.apiUrl}/admin/settings/playlists/${playlistId}/tracks`;
    return this._httpClient.delete(url, {body: {trackIds}}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }
}
